/**
 * Testimonial "book stack" carousel.
 * Front card via Slick fade; back stack peeks left.
 * On click: simple crossfade swap — all 4 cards stay visible, no flyer / hide.
 */
(function () {
  'use strict';

  var COMPACT_MQ = window.matchMedia('(max-width: 749px)');

  var PREV_ARROW_HTML =
    '<button type="button" class="slick-prev testimonial-book-carousel__arrow">' +
    '<span class="testimonial-book-carousel__arrow-icon testimonial-book-carousel__arrow-icon--prev" aria-hidden="true"></span>' +
    '</button>';
  var NEXT_ARROW_HTML =
    '<button type="button" class="slick-next testimonial-book-carousel__arrow">' +
    '<span class="testimonial-book-carousel__arrow-icon" aria-hidden="true"></span>' +
    '</button>';

  /**
   * @param {HTMLElement} slideEl
   * @returns {{ index: number, imgSrc: string, imgSrcset: string, imgAlt: string, label: string }}
   */
  function readSlideData(slideEl) {
    var img = slideEl.querySelector('.testimonial-book__card-img');
    var label = slideEl.querySelector('.testimonial-book__card-label');
    return {
      index: parseInt(slideEl.getAttribute('data-slide-index') || '0', 10),
      imgSrc: img ? img.getAttribute('src') || '' : '',
      imgSrcset: img ? img.getAttribute('srcset') || '' : '',
      imgAlt: img ? img.getAttribute('alt') || '' : '',
      label: label ? label.textContent.trim() : '',
    };
  }

  /**
   * @param {HTMLElement} root
   * @param {number} count
   * @returns {Array<HTMLElement | null>}
   */
  function buildSlideIndexMap(root, count) {
    /** @type {Array<HTMLElement | null>} */
    var map = [];
    var i;
    for (i = 0; i < count; i++) {
      map[i] = null;
    }
    root.querySelectorAll('.js-testimonial-book-slide').forEach(function (el) {
      var idx = parseInt(el.getAttribute('data-slide-index') || '0', 10);
      if (idx >= 0 && idx < count && !map[idx]) {
        map[idx] = el;
      }
    });
    return map;
  }

  /**
   * Swap back-card image without blanking — preload first when src changes.
   * @param {HTMLElement} backEl
   * @param {{ imgSrc: string, imgSrcset: string, imgAlt: string, label: string }} data
   */
  function fillBackCard(backEl, data) {
    var img = backEl.querySelector('.testimonial-book__back-img');
    var cap = backEl.querySelector('.testimonial-book__back-label');
    if (!img) {
      backEl.hidden = true;
      return;
    }

    if (cap) {
      cap.textContent = data.label;
    }

    if (!data.imgSrc) {
      img.setAttribute('hidden', '');
      img.removeAttribute('srcset');
      backEl.hidden = true;
      return;
    }

    backEl.hidden = false;
    img.removeAttribute('hidden');

    var currentSrc = img.getAttribute('src') || '';
    if (currentSrc === data.imgSrc) {
      if (data.imgSrcset) {
        img.setAttribute('srcset', data.imgSrcset);
      }
      img.setAttribute('alt', data.imgAlt);
      return;
    }

    var apply = function () {
      img.setAttribute('src', data.imgSrc);
      if (data.imgSrcset) {
        img.setAttribute('srcset', data.imgSrcset);
      } else {
        img.removeAttribute('srcset');
      }
      img.setAttribute('alt', data.imgAlt);
    };

    var preload = new Image();
    preload.onload = apply;
    preload.onerror = apply;
    if (data.imgSrcset) {
      preload.srcset = data.imgSrcset;
    }
    preload.src = data.imgSrc;
    if (preload.complete) {
      apply();
    }
  }

  /**
   * @param {any} slick
   * @param {number} slickIndex
   * @returns {number}
   */
  function resolveSlideDataIndex(slick, slickIndex) {
    var el = slick.$slides.get(slickIndex);
    if (!el) return 0;
    return parseInt(el.getAttribute('data-slide-index') || '0', 10);
  }

  /**
   * @param {any} slick
   * @param {Array<HTMLElement | null>} slideMap
   * @param {number} n
   * @param {HTMLElement} root
   * @param {number} [activeDataIndex]
   */
  function updateBackStack(slick, slideMap, n, root, activeDataIndex) {
    var backNodes = root.querySelectorAll('.testimonial-book__back-card');

    if (COMPACT_MQ.matches) {
      backNodes.forEach(function (node) {
        node.hidden = true;
      });
      return;
    }
    if (n < 2) {
      backNodes.forEach(function (node) {
        node.hidden = true;
      });
      return;
    }
    backNodes.forEach(function (node) {
      node.hidden = false;
      node.classList.remove('is-source-hidden');
    });

    var i =
      typeof activeDataIndex === 'number'
        ? activeDataIndex
        : resolveSlideDataIndex(slick, slick.currentSlide);

    var backs = root.querySelectorAll('.testimonial-book__back-card');
    var k;
    for (k = 0; k < 3; k++) {
      var idx = (i + k + 1) % n;
      var el = slideMap[idx];
      var data = el ? readSlideData(el) : { imgSrc: '', imgSrcset: '', imgAlt: '', label: '' };
      if (backs[k]) {
        backs[k].setAttribute('data-slide-ref', String(idx));
        backs[k].setAttribute(
          'aria-label',
          data.label ? 'Show review from ' + data.label : 'Show review'
        );
        fillBackCard(backs[k], data);
      }
    }
  }

  /**
   * @param {HTMLElement} root
   * @param {number} activeDataIndex
   */
  function updateThumbStrip(root, activeDataIndex) {
    var strip = root.querySelector('[data-testimonial-thumb-strip]');
    if (!strip) return;

    strip.querySelectorAll('.testimonial-book__thumb').forEach(function (thumb) {
      var idx = parseInt(thumb.getAttribute('data-goto-slide') || '0', 10);
      var isActive = idx === activeDataIndex;
      thumb.classList.toggle('is-active', isActive);
      thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  /**
   * @param {HTMLElement} root
   * @param {HTMLElement} slideEl
   */
  function updateDetailPanelFromSlide(root, slideEl) {
    if (!slideEl || !slideEl.hasAttribute('data-slide-index')) return;

    var title = slideEl.getAttribute('data-title') || '';
    var description = slideEl.getAttribute('data-description') || '';
    var name = slideEl.getAttribute('data-person-name') || '';
    var date = slideEl.getAttribute('data-review-date') || '';

    var panel = root.querySelector('.testimonial-book__detail');
    if (!panel) return;

    var h = panel.querySelector('.testimonial-book__detail-title');
    var p = panel.querySelector('.testimonial-book__detail-text');
    var nm = panel.querySelector('.testimonial-book__detail-name');
    var dt = panel.querySelector('.testimonial-book__detail-date');

    if (h) h.textContent = title;
    if (p) {
      p.textContent = '';
      if (description) {
        p.style.display = '';
        description.split('\n').forEach(function (line, i) {
          if (i > 0) p.appendChild(document.createElement('br'));
          p.appendChild(document.createTextNode(line));
        });
      } else {
        p.style.display = 'none';
      }
    }
    if (nm) nm.textContent = name;
    if (dt) {
      dt.textContent = date;
      dt.style.display = date ? '' : 'none';
    }
  }

  /**
   * @param {any} slick
   * @param {HTMLElement} root
   */
  function updateDetailPanel(slick, root) {
    var active = slick.$slides.get(slick.currentSlide);
    if (!active) return;
    updateDetailPanelFromSlide(root, active);
  }

  /**
   * @param {HTMLElement} root
   * @returns {JQuery<HTMLElement>}
   */
  function getArrowContainer(root) {
    var $ = window.jQuery;
    return $(root).find('.testimonial-book__arrows-host');
  }

  /**
   * @param {HTMLElement} root
   * @param {JQuery} $slider
   */
  function bindThumbNavigation(root, $slider) {
    var strip = root.querySelector('[data-testimonial-thumb-strip]');
    if (!strip) return;

    strip.querySelectorAll('.testimonial-book__thumb').forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        if (root.classList.contains('testimonial-book-carousel--swapping')) return;
        var idx = parseInt(thumb.getAttribute('data-goto-slide') || '0', 10);
        if (Number.isFinite(idx)) {
          $slider.slick('slickGoTo', idx);
        }
      });
    });
  }

  /**
   * Click a peeked stack card to bring it to the front.
   * @param {HTMLElement} root
   * @param {JQuery} $slider
   */
  function bindStackNavigation(root, $slider) {
    root.querySelectorAll('.testimonial-book__back-card').forEach(function (card) {
      card.style.cursor = 'pointer';
      card.setAttribute('role', 'button');
      card.setAttribute('tabindex', '0');

      var go = function () {
        if (root.classList.contains('testimonial-book-carousel--swapping')) return;
        var ref = parseInt(card.getAttribute('data-slide-ref') || '', 10);
        if (Number.isFinite(ref)) {
          $slider.slick('slickGoTo', ref);
        }
      };

      card.addEventListener('click', go);
      card.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          go();
        }
      });
    });
  }

  /**
   * @param {HTMLElement} root
   */
  function initSection(root) {
    var $ = window.jQuery;
    if (!$ || typeof $.fn.slick !== 'function') {
      root.classList.add('testimonial-book-carousel--ready');
      return;
    }

    var $slider = $(root).find('.js-testimonial-book-slider');
    if (!$slider.length || $slider.hasClass('slick-initialized')) {
      root.classList.add('testimonial-book-carousel--ready');
      return;
    }

    var count = parseInt(String(root.dataset.slideCount || '0'), 10);
    if (count < 1) {
      root.classList.add('testimonial-book-carousel--ready');
      return;
    }

    var slideMap = buildSlideIndexMap(root, count);
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var speed = parseInt(String(root.dataset.slideSpeed || '600'), 10);
    var initialSlide = parseInt(String(root.dataset.initialSlide || '0'), 10);
    var $dotsHost = $(root).find('.testimonial-book__dots-host');
    var showArrows = root.dataset.showArrows !== 'false' && count > 1;
    var showDots = root.dataset.showDots === 'true' && count > 1;

    var transitionSpeed = reducedMotion ? 0 : Number.isFinite(speed) ? speed : 500;
    var detailFadeOutMs = Math.max(80, Math.round(transitionSpeed * 0.35));
    var detailFadeInMs = Math.max(transitionSpeed - detailFadeOutMs, detailFadeOutMs);

    root.style.setProperty('--tbc-detail-fade-out-ms', detailFadeOutMs + 'ms');
    root.style.setProperty('--tbc-detail-fade-in-ms', detailFadeInMs + 'ms');
    root.style.setProperty('--tbc-transition-ms', Math.max(transitionSpeed, 400) + 'ms');

    /** @type {number | undefined} */
    var detailSwapTimer;
    /** @type {number | undefined} */
    var swapUnlockTimer;

    var slickOptions = {
      infinite: count > 1,
      fade: count > 1,
      slidesToShow: 1,
      slidesToScroll: 1,
      initialSlide: Number.isFinite(initialSlide) ? Math.min(initialSlide, count - 1) : 0,
      speed: transitionSpeed,
      cssEase: 'cubic-bezier(0.22, 1, 0.36, 1)',
      arrows: showArrows,
      dots: showDots,
      accessibility: true,
      swipe: count > 1,
      waitForAnimate: true,
      prevArrow: PREV_ARROW_HTML,
      nextArrow: NEXT_ARROW_HTML,
      appendArrows: getArrowContainer(root),
      appendDots: $dotsHost.length ? $dotsHost : $slider,
      adaptiveHeight: false,
    };

    if (count < 2) {
      slickOptions.infinite = false;
      slickOptions.arrows = false;
      slickOptions.dots = false;
      slickOptions.swipe = false;
    }

    function bindAria() {
      requestAnimationFrame(function () {
        var arrowScope = root.querySelector('.testimonial-book__arrows-host');
        if (!arrowScope) return;

        var prevBtn = arrowScope.querySelector('.slick-prev');
        var nextBtn = arrowScope.querySelector('.slick-next');
        if (prevBtn && root.dataset.labelPrev) {
          prevBtn.setAttribute('aria-label', root.dataset.labelPrev);
        }
        if (nextBtn && root.dataset.labelNext) {
          nextBtn.setAttribute('aria-label', root.dataset.labelNext);
        }
      });
    }

    function clearDetailSwapTimer() {
      if (detailSwapTimer) {
        window.clearTimeout(detailSwapTimer);
        detailSwapTimer = undefined;
      }
    }

    function clearSwapUnlockTimer() {
      if (swapUnlockTimer) {
        window.clearTimeout(swapUnlockTimer);
        swapUnlockTimer = undefined;
      }
    }

    $slider.on('beforeChange', function (_e, slick, currentSlide, nextSlide) {
      clearDetailSwapTimer();
      clearSwapUnlockTimer();

      var nextDataIndex = resolveSlideDataIndex(slick, nextSlide);
      updateThumbStrip(root, nextDataIndex);

      root.classList.add('testimonial-book-carousel--swapping');

      /* Update stack immediately — fillBackCard keeps old img until new is loaded */
      if (count > 1) {
        updateBackStack(slick, slideMap, count, root, nextDataIndex);
      }

      if (reducedMotion) return;

      var panel = root.querySelector('.testimonial-book__detail');
      if (panel) {
        panel.classList.add('testimonial-book__detail--is-changing');
      }

      detailSwapTimer = window.setTimeout(function () {
        var nextEl = slick.$slides.get(nextSlide);
        if (nextEl) {
          updateDetailPanelFromSlide(root, nextEl);
        }

        if (panel) {
          panel.classList.remove('testimonial-book__detail--is-changing');
        }
      }, Math.round(transitionSpeed * 0.4));
    });

    $slider.on('afterChange', function (_e, slick) {
      var activeIndex = resolveSlideDataIndex(slick, slick.currentSlide);
      updateThumbStrip(root, activeIndex);
      updateBackStack(slick, slideMap, count, root, activeIndex);

      clearSwapUnlockTimer();
      swapUnlockTimer = window.setTimeout(function () {
        root.classList.remove('testimonial-book-carousel--swapping');
      }, 80);

      if (reducedMotion) {
        clearDetailSwapTimer();
        updateDetailPanel(slick, root);
        var panelReduced = root.querySelector('.testimonial-book__detail');
        if (panelReduced) {
          panelReduced.classList.remove('testimonial-book__detail--is-changing');
        }
      }
    });

    $slider.on('init', function (_e, slick) {
      root.classList.add('testimonial-book-carousel--ready');
      bindAria();
      updateBackStack(slick, slideMap, count, root);
      updateDetailPanel(slick, root);
      updateThumbStrip(root, resolveSlideDataIndex(slick, slick.currentSlide));
    });

    try {
      $slider.slick(slickOptions);
      bindThumbNavigation(root, $slider);
      bindStackNavigation(root, $slider);

      var onCompactChange = function () {
        if (!$slider.hasClass('slick-initialized')) return;
        var slick = $slider.slick('getSlick');
        updateBackStack(slick, slideMap, count, root);
      };

      if (typeof COMPACT_MQ.addEventListener === 'function') {
        COMPACT_MQ.addEventListener('change', onCompactChange);
      } else if (typeof COMPACT_MQ.addListener === 'function') {
        COMPACT_MQ.addListener(onCompactChange);
      }
    } catch {
      root.classList.add('testimonial-book-carousel--ready');
    }
  }

  function initAll() {
    document.querySelectorAll('[data-testimonial-book-carousel]').forEach(function (root) {
      if (root instanceof HTMLElement) initSection(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var target = event.target;
    if (!(target instanceof HTMLElement)) return;
    var root = target.querySelector('[data-testimonial-book-carousel]');
    if (root) initSection(root);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var target = event.target;
    if (!(target instanceof HTMLElement)) return;
    var root = target.querySelector('[data-testimonial-book-carousel]');
    if (!root || !window.jQuery) return;
    var $slider = window.jQuery(root).find('.js-testimonial-book-slider');
    if ($slider.length && $slider.hasClass('slick-initialized')) {
      $slider.slick('unslick');
    }
    root.classList.remove('testimonial-book-carousel--ready');
  });
})();
